/**
 * Anchor Browser session REST client (ENG-7748).
 *
 * Thin, dependency-free (global `fetch`) wrapper over Anchor's session API. Auth
 * is the `anchor-api-key` header (NOT Bearer). The client covers exactly what
 * the manager's mint + re-mint lifecycle needs: create a profile-bound session
 * and end one. Mirrors `DeckClient` (packages/core/src/deck) in shape and error
 * handling.
 *
 * See `types.ts` for the VERIFICATION STATUS caveat on request/response shapes -
 * the two shape-sensitive spots (`buildCreateSessionBody`, `extractSessionId`)
 * are isolated so ENG-7749's live verification is a localized change.
 */

import {
  ANCHOR_API_BASE_URL,
  ANCHOR_API_KEY_HEADER,
  type AnchorSession,
  type CreateAnchorSessionInput,
} from './types.js';

export interface AnchorClientOptions {
  /** Anchor API key. Sent as the `anchor-api-key` header. */
  apiKey: string;
  /** Override the API base url (default `https://api.anchorbrowser.io/v1`). */
  baseUrl?: string;
  /** Per-request timeout in ms (default 30000). */
  timeoutMs?: number;
  /** Injectable fetch - defaults to the global. Used by tests. */
  fetchImpl?: typeof fetch;
}

/** Error thrown for any non-2xx Anchor response. Carries status + parsed body. */
export class AnchorApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'AnchorApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Build the `POST /sessions` request body from the mint input.
 *
 * SHAPE-SENSITIVE (// anchor-verify). Best-known placement from the ENG-5854
 * spike (`browser.profile.{name,persist}`) plus the ENG-5857 scoping comment
 * (`browser.dedicated_sticky_ip`). Anchor 400s on unknown keys, so a field is
 * only sent when set. Keep this the single source of truth for the body shape.
 */
export function buildCreateSessionBody(input: CreateAnchorSessionInput): Record<string, unknown> {
  const browser: Record<string, unknown> = {};
  if (input.profileName) {
    browser['profile'] = { name: input.profileName, persist: Boolean(input.persist) };
  }
  if (input.dedicatedStickyIp) {
    browser['dedicated_sticky_ip'] = true;
  }
  // max_duration / idle_timeout: best-known placement under a `timeout` object,
  // in minutes. Only sent when provided so an unknown-key 400 can't fire unless
  // the manager opts in. // anchor-verify: confirm field names/placement live.
  const timeout: Record<string, number> = {};
  if (typeof input.maxDurationMinutes === 'number') timeout['max_duration'] = input.maxDurationMinutes;
  if (typeof input.idleTimeoutMinutes === 'number') timeout['idle_timeout'] = input.idleTimeoutMinutes;

  const body: Record<string, unknown> = {};
  if (Object.keys(browser).length > 0) body['browser'] = browser;
  if (Object.keys(timeout).length > 0) body['session'] = { timeout };
  return body;
}

/**
 * Locate the session id in a create response. SHAPE-SENSITIVE (// anchor-verify):
 * the spike found the id by key-search rather than a pinned path, and Anchor may
 * wrap the payload in `{ data: {...} }`. We check the common shapes in order:
 * `data.id`, top-level `id`, `session_id`/`sessionId` anywhere shallow. Returns
 * null if none match (caller fails soft).
 */
export function extractSessionId(raw: unknown): string | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const data = obj['data'];
  const candidates: unknown[] = [
    data && typeof data === 'object' ? (data as Record<string, unknown>)['id'] : undefined,
    data && typeof data === 'object' ? (data as Record<string, unknown>)['session_id'] : undefined,
    obj['id'],
    obj['session_id'],
    obj['sessionId'],
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.length > 0) return c;
  }
  return null;
}

/**
 * Locate the human live-view URL in a create response. SHAPE-SENSITIVE
 * (// anchor-verify): the spike found it by key-search (`/live.?view/i`), and
 * Anchor may nest it under `data`. Checks the common snake/camel keys under
 * `data` then top-level. Returns null when absent.
 */
export function extractLiveViewUrl(raw: unknown): string | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  const data = obj['data'];
  const dataObj = data && typeof data === 'object' ? (data as Record<string, unknown>) : undefined;
  const candidates: unknown[] = [
    dataObj?.['live_view_url'],
    dataObj?.['liveViewUrl'],
    obj['live_view_url'],
    obj['liveViewUrl'],
  ];
  for (const c of candidates) {
    if (typeof c === 'string' && c.length > 0) return c;
  }
  return null;
}

export class AnchorSessionClient {
  private readonly apiKey: string;
  private readonly baseUrl: string;
  private readonly timeoutMs: number;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: AnchorClientOptions) {
    if (!opts.apiKey) {
      throw new Error('AnchorSessionClient requires an apiKey');
    }
    this.apiKey = opts.apiKey;
    // Trim a trailing slash so path joins are predictable.
    this.baseUrl = (opts.baseUrl ?? ANCHOR_API_BASE_URL).replace(/\/+$/, '');
    this.timeoutMs = opts.timeoutMs ?? 30_000;
    this.fetchImpl = opts.fetchImpl ?? fetch;
  }

  /**
   * Mint a session (`POST /sessions`). With a `profileName` the session resumes
   * that profile's authenticated state; without one it is a fresh stateless
   * browser. Throws {@link AnchorApiError} on a non-2xx, or a plain Error when
   * the response carries no locatable session id.
   */
  async createSession(input: CreateAnchorSessionInput = {}): Promise<AnchorSession> {
    const raw = await this.request<unknown>('POST', '/sessions', buildCreateSessionBody(input));
    const sessionId = extractSessionId(raw);
    if (!sessionId) {
      throw new Error('Anchor createSession returned no locatable session id');
    }
    const liveViewUrl = extractLiveViewUrl(raw);
    return { sessionId, ...(liveViewUrl ? { liveViewUrl } : {}), raw };
  }

  /**
   * Snapshot a running session's authenticated state into a named profile
   * (`POST /profiles` with `source: 'session'`). This is the explicit SAVE step
   * for onboarding - separating it from `endSession` means an aborted onboarding
   * can just end the session and persist NOTHING (a persist-on-end session would
   * flush partial/unauthenticated state and could clobber a good profile).
   * // anchor-verify: body shape from the ENG-5854 spike (verified live there).
   */
  async saveProfileFromSession(
    name: string,
    sessionId: string,
    opts: { dedicatedStickyIp?: boolean } = {},
  ): Promise<void> {
    await this.request<unknown>('POST', '/profiles', {
      name,
      source: 'session',
      session_id: sessionId,
      ...(opts.dedicatedStickyIp ? { dedicated_sticky_ip: true } : {}),
    });
  }

  /**
   * End a session (`DELETE /sessions/:id`). Best-effort by contract: a 404 (the
   * session already expired) is treated as success - the goal state (no live
   * session) is reached either way.
   */
  async endSession(sessionId: string): Promise<void> {
    try {
      await this.request<void>('DELETE', `/sessions/${encodeURIComponent(sessionId)}`);
    } catch (err) {
      if (err instanceof AnchorApiError && err.status === 404) return;
      throw err;
    }
  }

  // --- internals ----------------------------------------------------------

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);
    // The timeout must cover the body read too, not just the fetch - a hung
    // response stream would otherwise never time out. Both share the signal;
    // the timer is cleared in `finally`.
    let res: Response;
    let text: string;
    try {
      res = await this.fetchImpl(url, {
        method,
        headers: {
          [ANCHOR_API_KEY_HEADER]: this.apiKey,
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });
      text = await res.text();
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      throw new AnchorApiError(0, `Anchor request failed: ${reason}`, undefined);
    } finally {
      clearTimeout(timer);
    }

    let parsed: unknown;
    try {
      parsed = text ? JSON.parse(text) : undefined;
    } catch {
      parsed = text;
    }

    if (!res.ok) {
      const detail =
        typeof parsed === 'object' && parsed !== null && 'error' in parsed
          ? String((parsed as Record<string, unknown>).error)
          : res.statusText;
      throw new AnchorApiError(res.status, `Anchor returned ${res.status}: ${detail}`, parsed);
    }

    return parsed as T;
  }
}
